import { ArrowDown, ArrowUp, CheckCircle2, ShieldCheck } from "lucide-react";

const PRIORITIES = ["HIGH_PRIORITY", "RECOMMENDED", "MONITOR_MAINTAIN"];
const PRIORITY_LABELS = {
  HIGH_PRIORITY: "HIGH PRIORITY", RECOMMENDED: "RECOMMENDED",
  MONITOR_MAINTAIN: "MONITOR / MAINTAIN",
};
const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-700",
  reviewed: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
};

/** Recommendation Review — staff may reword, reprioritise and reorder what the
    engine produced. They can never add a recommendation with no finding behind
    it, and the original rule output stays on the record for audit. */
export function ReportReviewPanel({ editor, setEditor, onSave, onSetStatus }) {
  const cards = editor.recommendation_cards || [];
  const status = editor.review_status || "draft";

  const patchCard = (type, patch) => setEditor({
    ...editor,
    recommendation_cards: cards.map((c) => (c.recommendation_type === type
      ? { ...c, ...patch } : c)),
  });
  const move = (idx, dir) => {
    const next = [...cards];
    const to = idx + dir;
    if (to < 0 || to >= next.length) return;
    [next[idx], next[to]] = [next[to], next[idx]];
    setEditor({ ...editor, recommendation_cards: next });
  };

  return (
    <div className="mt-4 rounded-xl border border-cyan-200 bg-cyan-50/40 p-4"
      data-testid="admin-thc-review-panel">
      <div className="flex flex-wrap items-center gap-3">
        <ShieldCheck className="h-4 w-4 text-cyan-700" />
        <b className="text-slate-800">Recommendation review</b>
        <span data-testid="admin-thc-review-status"
          className={`rounded-full px-2 py-0.5 text-[10px] font-extrabold uppercase ${STATUS_STYLE[status]}`}>
          {status}
        </span>
        {editor.engine_version && (
          <span className="text-[10px] text-slate-400">{editor.engine_version}</span>
        )}
        {editor.locked && (
          <span className="text-[11px] font-bold text-slate-500">
            Sent — reissue a new version to change anything
          </span>
        )}
      </div>

      {!!(editor.findings || []).length && (
        <div className="mt-3" data-testid="admin-thc-review-findings">
          <div className="text-[11px] font-bold uppercase text-slate-500">Recorded findings</div>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {editor.findings.map((f) => (
              <span key={f.code} className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-600 ring-1 ring-slate-200">
                {f.label}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="mt-3">
        <div className="text-[11px] font-bold uppercase text-slate-500">
          Customer-facing technician assessment (the raw technician note is never printed)
        </div>
        <textarea data-testid="admin-thc-review-assessment" rows={4} disabled={editor.locked}
          value={editor.customer_facing_assessment || ""}
          onChange={(e) => setEditor({ ...editor, customer_facing_assessment: e.target.value })}
          className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-[13px]" />
      </div>

      <div className="mt-3 space-y-3">
        {cards.map((c, i) => (
          <div key={c.recommendation_type} data-testid={`admin-thc-review-card-${c.recommendation_type}`}
            className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[10px] font-extrabold uppercase text-slate-400">
                Priority {i + 1}
              </span>
              <b className="text-slate-800">{c.title}</b>
              <select data-testid={`admin-thc-review-priority-${c.recommendation_type}`}
                disabled={editor.locked} value={c.priority}
                onChange={(e) => patchCard(c.recommendation_type, {
                  priority: e.target.value,
                  priority_label: PRIORITY_LABELS[e.target.value],
                })}
                className="rounded-md border border-slate-300 px-2 py-1 text-[11px] font-bold">
                {PRIORITIES.map((p) => <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>)}
              </select>
              <span className="text-[10px] text-slate-400">{c.cta_label}</span>
              <div className="ml-auto flex gap-1">
                <button type="button" disabled={editor.locked || i === 0}
                  data-testid={`admin-thc-review-up-${c.recommendation_type}`}
                  onClick={() => move(i, -1)}
                  className="rounded-md border border-slate-300 p-1 disabled:opacity-30">
                  <ArrowUp className="h-3 w-3" />
                </button>
                <button type="button" disabled={editor.locked || i === cards.length - 1}
                  data-testid={`admin-thc-review-down-${c.recommendation_type}`}
                  onClick={() => move(i, 1)}
                  className="rounded-md border border-slate-300 p-1 disabled:opacity-30">
                  <ArrowDown className="h-3 w-3" />
                </button>
              </div>
            </div>
            <textarea data-testid={`admin-thc-review-reason-${c.recommendation_type}`}
              rows={2} disabled={editor.locked} value={c.reason || ""}
              onChange={(e) => patchCard(c.recommendation_type, { reason: e.target.value })}
              className="mt-2 w-full rounded-lg border border-slate-300 p-2 text-[12px]" />
            <input data-testid={`admin-thc-review-scope-${c.recommendation_type}`}
              disabled={editor.locked} value={c.scope || ""}
              onChange={(e) => patchCard(c.recommendation_type, { scope: e.target.value })}
              className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-[12px] font-semibold" />
            <div className="mt-1 text-[10px] text-slate-400">
              From: {(c.source_findings || []).join(", ")}
            </div>
          </div>
        ))}
        {!cards.length && (
          <div className="text-[12px] text-slate-500">
            No structured recommendations on this version — it was generated before the
            action plan existed. Reissue the report to build one.
          </div>
        )}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <button data-testid="admin-thc-review-save" disabled={editor.locked}
          onClick={onSave}
          className="rounded-full bg-slate-900 px-4 py-2 text-[11px] font-extrabold text-white disabled:opacity-40">
          SAVE REVIEW
        </button>
        <button data-testid="admin-thc-review-mark-reviewed" disabled={editor.locked}
          onClick={() => onSetStatus("reviewed")}
          className="rounded-full border border-amber-400 px-4 py-2 text-[11px] font-extrabold text-amber-700 disabled:opacity-40">
          MARK REVIEWED
        </button>
        <button data-testid="admin-thc-review-approve" disabled={editor.locked}
          onClick={() => onSetStatus("approved")}
          className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-4 py-2 text-[11px] font-extrabold text-white disabled:opacity-40">
          <CheckCircle2 className="h-3 w-3" /> APPROVE FOR SENDING
        </button>
        {editor.action_url && (
          <a href={editor.action_url} target="_blank" rel="noreferrer"
            data-testid="admin-thc-review-action-link"
            className="rounded-full border border-cyan-300 px-4 py-2 text-[11px] font-extrabold text-cyan-700">
            PREVIEW ACTION PAGE
          </a>
        )}
      </div>
    </div>
  );
}
